<template>
  <div class="ModifyInsuranceModal">
    <v-form
      ref="$form"
      validate-on="input lazy"
    >
      <!-- ▼ HOW MANY PETS ▼ -->
      <BaseSelect
        v-if="isModify"
        id="how-many-pets"
        :label="t('pets.how-many-form.modify-legend')"
        :items="howManyPetsModifyOptions"
        item-title="label"
        item-value="value"
        density="compact"
        variant="outlined"
        v-model="howManyPetsOption"
        @update:model-value="onChangeHowManyPets()"
      />

      <template v-else>
        <RadioButtonGroup
          id="how-many-pets"
          :key="howManyPetsOption"
          :legend="t('pets.how-many-form.calculator-legend')"
          :value="howManyPetsOption"
          :options="createSubOptions(1)"
          v-model="howManyPetsOption"
          flex-direction="row"
          :small="true"
          @update:model-value="onChangeHowManyPets()"
        />

        <BaseSelect
          v-if="howManyPetsOption === 'more'"
          id="more-people"
          v-model="howManyPetsCustomOption"
          :items="howManyPetsOptions"
          @update:model-value="onChangeHowManyPets()"
        />
      </template>
      <!-- ▲ HOW MANY PETS ▲ -->

      <!-- ▼ START DATE ▼ -->
      <DatePickerComponent
        id="start-date"
        :label="
          isModify
            ? t('rater.common.modify-search-view.start-date')
            : t('rater.common.start-date.form-legend')
        "
        :modelValue="newAnswers.startDate"
        @update:modelValue="updateStartDate"
        :datePickerVisible="!isModify"
        :min-date="minDateInit"
        :max-date="addDaysToDate(configurations.maxDays)"
        :rules="[required]"
      />
      <!-- ▲ START DATE ▲ -->

      <!-- ▼ PET INFO ▼ -->
      <template
        v-for="(pet, i) in newAnswers.pets"
        :key="'pet_' + (i + 1)"
      >
        <!-- ▼ NAME ▼ -->
        <BaseInput
          :class="extraPtClass"
          :label="t('pets.pets-names-form.name-label', { index: i + 1 })"
          :id="'name_' + (i + 1)"
          v-model="pet.name"
          :value="pet.name"
          :maxlength="30"
          :placeholder="t('pets.pets-names-form.placeholder')"
          :rules="[
            validationRules.required,
            validationRules.characterLimit,
            validationRules.validName
          ]"
        />
        <!-- ▲ NAME ▲ -->
        <!-- ▼ ADDITIONAL COVERAGES ▼ -->
        <template v-if="petsSelectedsIndexes !== undefined">
          <p class="add-guarantees-legend">{{ t('pets.what-optional-guarantees') }}</p>
          <template
            v-for="addCoverage in petsStore.additionalGuaranteesItems"
            :key="addCoverage.sumInsured + i"
          >
            <BaseCheckBox
              class="extra-mb"
              :id="addCoverage.coverageCode + i"
              :label="addCoverage.text"
              :description="addCoverage.description"
              v-model="petsSelectedsIndexes[i][addCoverage.coverageCode].selected"
            />
            <AdvantageSliderComponent
              v-if="addCoverage.range && petsSelectedsIndexes[i][addCoverage.coverageCode].selected"
              :id="`coverage-slider-${i}-${addCoverage.coverageCode}`"
              class="extra-mb"
              @update:modelValue="
                (newValue) => updateCivilLiabilityAmount(i, addCoverage.coverageCode, newValue)
              "
              :value="
                Number(
                  petsSelectedsIndexes[i][addCoverage.coverageCode].rangeValue
                    ? petsSelectedsIndexes[i][addCoverage.coverageCode].rangeValue
                    : addCoverage.range[0]
                )
              "
              :min="Number(addCoverage.range[0])"
              :max="Number(addCoverage.range[addCoverage.range.length])"
              :units="CURRENCY"
              :type="'indexed'"
              :defaultIndex="
                petsSelectedsIndexes[i][addCoverage.coverageCode].rangeValue
                  ? addCoverage.range
                      .map((v) => Number(v))
                      .findIndex(
                        (a) => a === petsSelectedsIndexes[i][addCoverage.coverageCode].rangeValue
                      )
                  : 0
              "
              :indexed-values="addCoverage.range.map((v) => Number(v))"
            />
          </template>
        </template>
        <!-- ▲ ADDITIONAL COVERAGES ▲ -->
      </template>
      <!-- ▲ PET INFO ▲ -->
      <hr class="last-hr" />
      <!-- ▼ ACTION BTNS ▼ -->
      <div class="actions">
        <BaseButton @click="submit">
          {{
            t(
              'rater.base.' +
                (isModify ? (onlyNameChanged ? 'save' : 'recalculate-submit') : 'submit')
            )
          }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
        <!-- ▲ ACTION BTNS ▲ -->
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
  import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'
  import Coverage from '@/modules/common/common/models/MID/Coverage'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
  import PetsConfigurations from '@/modules/pets/common/models/APP/PetsConfigurations'
  import { arrayRange } from '@/utils/array.utils'
  import { addDaysToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { PetRater } from '../../models/APP/PetRater'
  import type { PetsAdditionalCoverageItem } from '../../models/APP/PetsAdditionalCoverageItem'
  import PetsRaterAnswers from '../../models/APP/PetsRaterAnswers'
  import { usePetsStore } from '../../stores'

  interface PropsInterface {
    answers: PetsRaterAnswers
    configurations: PetsConfigurations
    isModify?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), { isModify: true })

  const emit = defineEmits(['submit', 'close'])

  const MIN_CUSTOM_PEOPLE = 3

  const { t } = useI18n()
  const { required, validName, characterLimit } = useRules()
  const petsStore = usePetsStore()
  const sharedGtmDataStore = useSharedGtmDataStore()

  const $form = ref()
  const newAnswers = ref(new PetsRaterAnswers(props.answers))
  const petsSelectedsIndexes = ref<any[]>([])
  const onlyNameChanged = ref(true)
  let initialPetsSelectedsIndexes: any[]

  const howManyPetsOption = ref(
    newAnswers.value.pets?.length >= MIN_CUSTOM_PEOPLE
      ? props.isModify
        ? newAnswers.value.pets.length
        : 'more'
      : String(newAnswers.value.pets?.length ?? 1)
  )

  const howManyPetsCustomOption = ref(
    String(
      newAnswers.value.pets?.length >= MIN_CUSTOM_PEOPLE
        ? newAnswers.value.pets.length
        : MIN_CUSTOM_PEOPLE
    )
  )

  const howManyPetsOptions = computed(() => {
    return arrayRange(MIN_CUSTOM_PEOPLE, props.configurations.maxInsurances).map((label) => ({
      label: String(label),
      value: String(label)
    }))
  })

  const howManyPetsModifyOptions = computed(() => {
    return arrayRange(1, props.configurations.maxInsurances).map((label) => ({
      label: String(label),
      value: String(label)
    }))
  })

  const minDateInit = computed(() => {
    const tomorrow = addDaysToDate(1, new Date())
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  })

  const extraPtClass = computed(() => ({ 'extra-pt': !props.isModify }))

  const validationRules = {
    required,
    validName,
    characterLimit: (value: string) => characterLimit(value, 30)
  }

  const createSubOptions = (min: number) => {
    return [
      { label: min.toString(), value: String(min) },
      { label: (min + 1).toString(), value: String(min + 1) },
      { label: t('rater.common.more'), value: 'more' }
    ]
  }

  function onChangeHowManyPets() {
    let petsN = Number(howManyPetsOption.value)
    if (isNaN(petsN)) {
      petsN = Number(howManyPetsCustomOption.value)
    }
    if (!isNaN(petsN)) {
      if (petsN < newAnswers.value.pets.length) {
        const positions = -(newAnswers.value.pets.length - petsN)
        petsSelectedsIndexes.value.splice(positions)
        newAnswers.value.pets.splice(positions)
      } else if (petsN > newAnswers.value.pets.length) {
        petsSelectedsIndexes.value.push(
          ...Array.from({ length: petsN - newAnswers.value.pets.length }, () => {
            const item: {
              [key: string]: PetsAdditionalCoverageItem
            } = {}
            petsStore.additionalGuaranteesItems?.forEach((v) => {
              item[v.coverageCode] = {
                sumInsured: v.sumInsured,
                rangeValue: undefined,
                selected: false
              }
            })
            return item
          })
        )
        newAnswers.value.pets.push(
          ...Array.from({ length: petsN - newAnswers.value.pets.length }, () => new PetRater())
        )
      }
    }
  }

  function updateCivilLiabilityAmount(
    index: number,
    additionalGuaranteeIndex: string,
    newValue: number
  ) {
    petsSelectedsIndexes.value[index][additionalGuaranteeIndex].rangeValue = newValue
  }

  function updateStartDate(newValue: Date) {
    newAnswers.value.startDate = newValue
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return

    let isCoverageSelected = false
    newAnswers.value.pets.forEach((pet, i) => {
      const additionalCoverages = Object.keys(petsSelectedsIndexes.value[i])
        .filter((k) => petsSelectedsIndexes.value[i][k].selected)
        .map((k) => {
          isCoverageSelected = true
          const coverage = petsSelectedsIndexes.value[i][k]
          return {
            coverageCode: k,
            selected: coverage.selected ?? false,
            sumInsured: coverage.sumInsured,
            rangeValue: coverage.rangeValue
          }
        })
      pet.additionalCoverages = additionalCoverages
      pet.coverages =
        petsStore.guaranteesItems?.map(
          (gi) =>
            new Coverage({
              coverageCode: gi.coverageCode,
              sumInsured: gi.sumInsured
            })
        ) ?? []
      sharedGtmDataStore.additionalCoverages = isCoverageSelected
      return pet
    })

    emit(
      'submit',
      props.isModify
        ? { answers: newAnswers.value, onlyNameChanged: onlyNameChanged.value }
        : newAnswers.value
    )
  }

  onBeforeMount(async () => {
    if (!newAnswers.value.pets?.length) {
      newAnswers.value.pets = [new PetRater()]
      howManyPetsOption.value = '1'
    }
    if (!newAnswers.value.startDate) {
      newAnswers.value.startDate = addDaysToDate(1)
    }
    await petsStore.getCoverages()
    initialPetsSelectedsIndexes = Array.from({ length: newAnswers.value.pets.length }, (_, i) => {
      const item: {
        [key: string]: PetsAdditionalCoverageItem
      } = {}
      petsStore.additionalGuaranteesItems?.forEach((v) => {
        const addCov = newAnswers.value.pets[i].additionalCoverages?.find(
          (ac) => ac.coverageCode === v.coverageCode
        )
        item[v.coverageCode] = {
          sumInsured: addCov?.sumInsured ?? v.sumInsured,
          rangeValue: addCov?.rangeValue,
          selected: addCov?.selected ?? false
        }
      })
      return item
    })

    petsSelectedsIndexes.value = structuredClone(initialPetsSelectedsIndexes)

    if (props.isModify) {
      watch(
        () => [
          newAnswers.value.pets.length,
          newAnswers.value.startDate,
          petsSelectedsIndexes.value
        ],
        () => {
          if (
            newAnswers.value.pets.length !== props.answers.pets.length ||
            props.answers.startDate !== newAnswers.value.startDate ||
            !!initialPetsSelectedsIndexes!.find(
              (ipsi, i) =>
                petsSelectedsIndexes.value[i] &&
                Object.keys(ipsi).find(
                  (key) =>
                    petsSelectedsIndexes.value[i][key]?.selected !== ipsi[key].selected ||
                    petsSelectedsIndexes.value[i][key]?.rangeValue !== ipsi[key].rangeValue
                )
            )
          ) {
            // changed any other value, needs to recalculate the pricing
            onlyNameChanged.value = false
          } else {
            // only changed the name, just save it and do not recalculate the pricing
            onlyNameChanged.value = true
          }
        },
        { deep: true }
      )
    }
  })
</script>

<style lang="scss" scoped>
  .ModifyInsuranceModal {
    .tariff-subtitle {
      color: var(--pricing-subtitle-color);
      font-size: $font-size-l;
      text-transform: uppercase;
      margin-bottom: $spacing-xs;

      @include breakpoint-md {
        text-align: left;
      }
    }

    label {
      color: $color-neutral-dark;
    }

    .form-legend {
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-42;
      margin-bottom: $spacing-xl;
      text-align: left;
      text-wrap: balance;
    }

    .actions {
      margin-top: $spacing-2;
      padding-bottom: $spacing-2xl;
    }

    .start-date_form-field {
      margin-top: $spacing-2;
    }

    .add-guarantees-legend {
      margin-bottom: $spacing-m;
      color: $color-neutral-dark;
    }

    .AdvantageSliderComponent {
      max-width: $size-277 !important;
      margin: 0 auto;
    }

    .last-hr {
      margin-top: unset;
    }

    .extra-mb {
      margin-bottom: $spacing-xl;
    }

    .extra-pt {
      padding-top: $spacing-xl;
    }
  }
</style>
